import { useCallback, useState } from 'react';

/** Display preferences of one calendar (a board, a workspace or the planner); never in the URL. */
export interface CalendarPrefs {
  /** Hide the events that have ended (presentation only). */
  hidePast: boolean;
  /** Show the daily workload next to the dates. */
  workload: boolean;
  /** Show archived tasks (with their dates, marked as archived); on unless switched off. */
  showArchived: boolean;
}

const PREFIX = 'zadok.calendarPrefs.';
const DEFAULTS: CalendarPrefs = { hidePast: false, workload: false, showArchived: true };

export function readCalendarPrefs(storageKey: string): CalendarPrefs {
  try {
    const raw = window.localStorage.getItem(`${PREFIX}${storageKey}`);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Record<keyof CalendarPrefs, unknown>>;
    return {
      hidePast: parsed.hidePast === true,
      workload: parsed.workload === true,
      // on unless the person explicitly switched it off
      showArchived: parsed.showArchived !== false,
    };
  } catch {
    return DEFAULTS;
  }
}

function writeCalendarPrefs(storageKey: string, prefs: CalendarPrefs): void {
  try {
    window.localStorage.setItem(`${PREFIX}${storageKey}`, JSON.stringify(prefs));
  } catch {
    // Storage unavailable: the choice just is not remembered.
  }
}

/** The preferences of one calendar, remembered per scope in this browser. */
export function useCalendarPrefs(
  storageKey: string,
): [CalendarPrefs, (patch: Partial<CalendarPrefs>) => void] {
  const [state, setState] = useState(() => ({
    key: storageKey,
    prefs: readCalendarPrefs(storageKey),
  }));
  let current = state;
  if (state.key !== storageKey) {
    current = { key: storageKey, prefs: readCalendarPrefs(storageKey) }; // another calendar
    setState(current);
  }
  const key = current.key;
  const set = useCallback(
    (patch: Partial<CalendarPrefs>) => {
      setState((previous) => {
        const prefs = { ...previous.prefs, ...patch };
        writeCalendarPrefs(key, prefs);
        return { key, prefs };
      });
    },
    [key],
  );
  return [current.prefs, set];
}
